import { RootState } from '@/app/store';
import { isData } from '@/shared/helpers/websocket';
import { socket, waitForOpenConnection } from '@/shared/websocket/socket';
import { SocketChannelName, TradeItem, TradeList, camelizeNestedKeys } from '@cex/shared-modules';
import { createEntityAdapter, createSelector } from '@reduxjs/toolkit';
import { mainEmptySplitApi } from '@/features/main/mainApi';

const tradesAdapter = createEntityAdapter<TradeItem>({
  selectId: (trades) => trades[0].market
});

const initialState = tradesAdapter.getInitialState();

export const tradesApi = mainEmptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    getTrades: builder.query({
      query: (market) => `${market}/market/trades?limit=80`,
      providesTags: (result, error, arg) => [{ type: 'Trades', id: arg }],
      transformResponse: (res: TradeList | null) => {
        if (!res) {
          return initialState;
        }
        const camelizedResponse = camelizeNestedKeys(res);
        return tradesAdapter.setOne(initialState, camelizedResponse);
      },
      async onCacheEntryAdded(arg, { updateCachedData, cacheDataLoaded, cacheEntryRemoved }) {
        const market = arg;
        try {
          // Websocket implementation: https://redux-toolkit.js.org/rtk-query/usage/streaming-updates#websocket-chat-api
          await cacheDataLoaded;
          await waitForOpenConnection();
          socket.subscribe(SocketChannelName.PUBLIC_TRADE, market);

          const listener = (event: MessageEvent) => {
            const data = JSON.parse(event.data);
            if (!isData(data) || data.subchan_name !== market || data.chan_name !== SocketChannelName.PUBLIC_TRADE) {
              return;
            }

            updateCachedData((draft) => {
              tradesAdapter.upsertOne(draft, data.data);
            });
          };

          socket.addEventListener('message', listener);
        } catch (e) {
          console.error('TRADES CACHE', e);
        }
        await cacheEntryRemoved;
        socket.close();
      }
    })
  })
});

// @ts-ignore
const selectTradesResult = tradesApi.endpoints.getTrades.select();

export const selectTradesData = createSelector(selectTradesResult, (apiSlice) => apiSlice.data);

export const { selectAll: selectAllTrades, selectById: selectTradeById } = tradesAdapter.getSelectors(
  (state: RootState) => selectTradesData(state) ?? initialState
);

export const { useGetTradesQuery } = tradesApi;
